import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Camera, Keyboard } from 'lucide-react';

interface BarcodeScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (code: string) => void;
  title?: string;
  description?: string;
}

// Check if the native BarcodeDetector API is available
const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

export function BarcodeScanner({ open, onOpenChange, onScan, title = 'Scan Barcode', description }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const detectorRef = useRef<InstanceType<typeof BarcodeDetector> | null>(null);
  const lastScannedRef = useRef<string | null>(null);
  const cooldownRef = useRef(false);

  const [mode, setMode] = useState<'camera' | 'manual'>(hasBarcodeDetector ? 'camera' : 'manual');
  const [manualCode, setManualCode] = useState('');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const handleScan = useCallback((code: string) => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed || cooldownRef.current) return;
    // Debounce: same code within 2s is ignored
    if (lastScannedRef.current === trimmed) return;
    lastScannedRef.current = trimmed;
    cooldownRef.current = true;
    setTimeout(() => { cooldownRef.current = false; lastScannedRef.current = null; }, 2000);

    stopCamera();
    onScan(trimmed);
    onOpenChange(false);
  }, [stopCamera, onScan, onOpenChange]);

  const startCamera = useCallback(async () => {
    if (!hasBarcodeDetector) {
      setMode('manual');
      return;
    }
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // @ts-expect-error — BarcodeDetector not yet in TypeScript lib
      detectorRef.current = new BarcodeDetector({
        formats: ['code_128', 'code_39', 'qr_code', 'ean_13', 'ean_8', 'data_matrix', 'upc_a', 'upc_e'],
      });

      setScanning(true);

      const scanFrame = async () => {
        if (!videoRef.current || !detectorRef.current || videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(scanFrame);
          return;
        }
        try {
          const results = await detectorRef.current.detect(videoRef.current);
          if (results.length > 0) {
            handleScan(results[0].rawValue);
            return; // don't schedule next frame — dialog will close
          }
        } catch {
          // detection errors are normal (e.g., no barcode in frame) — just continue
        }
        rafRef.current = requestAnimationFrame(scanFrame);
      };
      rafRef.current = requestAnimationFrame(scanFrame);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setCameraError(msg.includes('Permission') || msg.includes('NotAllowed')
        ? 'Camera permission denied. Use manual input instead.'
        : 'Camera not available. Use manual input instead.');
      setMode('manual');
    }
  }, [handleScan]);

  // Start/stop camera when dialog opens/closes
  useEffect(() => {
    if (open && mode === 'camera') {
      startCamera();
    }
    return () => {
      if (!open) stopCamera();
    };
  }, [open, mode, startCamera, stopCamera]);

  // Clean up on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopCamera(); setManualCode(''); } onOpenChange(v); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            {mode === 'camera' ? <Camera className="h-4 w-4" /> : <Keyboard className="h-4 w-4" />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {mode === 'camera' ? (
          <div className="relative bg-black">
            <video
              ref={videoRef}
              className="w-full aspect-video object-cover"
              playsInline
              muted
            />
            {/* Viewfinder overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-32 border-2 border-white/70 rounded-lg relative">
                {/* Corner marks */}
                {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(corner => (
                  <div
                    key={corner}
                    className={`absolute w-4 h-4 border-white border-2
                      ${corner.includes('top') ? 'top-0' : 'bottom-0'}
                      ${corner.includes('left') ? 'left-0 border-r-0 border-b-0' : 'right-0 border-l-0 border-b-0'}
                      ${corner.includes('bottom') && corner.includes('left') ? '!border-t-0 !border-r-0 !border-b-2 !border-l-2' : ''}
                      ${corner.includes('bottom') && corner.includes('right') ? '!border-t-0 !border-l-0 !border-b-2 !border-r-2' : ''}
                    `}
                  />
                ))}
                {scanning && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-0.5 bg-red-400 animate-bounce opacity-70" />
                  </div>
                )}
              </div>
            </div>
            <p className="absolute bottom-2 left-0 right-0 text-center text-white/70 text-xs">
              Point camera at barcode or QR code
            </p>
          </div>
        ) : (
          <form onSubmit={handleManualSubmit} className="px-4 pb-2 space-y-3">
            {cameraError && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">{cameraError}</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="manual-barcode">Enter code manually</Label>
              <div className="flex gap-2">
                <Input
                  id="manual-barcode"
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value.toUpperCase())}
                  placeholder="e.g. M-001 or SP-042"
                  autoFocus
                  className="font-mono"
                />
                <Button type="submit" disabled={!manualCode.trim()}>Go</Button>
              </div>
            </div>
          </form>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t">
          {hasBarcodeDetector ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (mode === 'camera') { stopCamera(); setMode('manual'); }
                else { setMode('camera'); }
              }}
            >
              {mode === 'camera' ? <><Keyboard className="h-3.5 w-3.5 mr-1.5" />Enter manually</> : <><Camera className="h-3.5 w-3.5 mr-1.5" />Use camera</>}
            </Button>
          ) : <span />}
          <Button variant="ghost" size="sm" onClick={() => { stopCamera(); onOpenChange(false); }}>
            <X className="h-3.5 w-3.5 mr-1.5" />Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
